import { AfterContentInit, Component, ContentChild, ElementRef, HostListener, Input, TemplateRef, ViewChild } from '@angular/core';
import { MatAutocomplete, MatAutocompleteActivatedEvent, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { FormContainerKind, ItemWithID } from '@sb/library/base';
import { Observable, Subscription, timer } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { FormFieldSelectBaseComponent } from '../form-field-select-base.component';
import { FormFieldSelectItemComponent, IItemGroup } from '../form-field-select-item.component';

@Component({
    selector: 'lib-input-select',
    templateUrl: './form-field-select.component.html',
    styleUrls: ['./form-field-select.component.scss']
})
export class FormFieldSelectComponent<T extends ItemWithID | string> extends FormFieldSelectBaseComponent<T, T | null> implements AfterContentInit {

    @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;
    @ViewChild(MatAutocomplete) autocomplete!: MatAutocomplete;
    @ContentChild('triggerTemplate') triggerTemplate!: TemplateRef<any>;
    @ViewChild('input') input?: ElementRef;

    @Input() kindOverride: FormContainerKind = FormContainerKind.Text;

   

    private _filteredItemsByGroup!: Observable<Map<IItemGroup | null, FormFieldSelectItemComponent<T>[]>>;
    private _allItems: FormFieldSelectItemComponent<T>[] = [];

    private _selectedItem: FormFieldSelectItemComponent<T> | null = null;
    private _selectedItemHTML: string = '';

    private _resultSetEmpty: boolean = false;
    private _userTyping: boolean = false;
    private _itemSelectedFromDropdown: boolean = false;
    private _scrollToOptionFromOpenPanel: boolean = false;
    private _waitingForDoubleClick: boolean = false;
    private _updatingViewControlValueManually: boolean = false;

    private _timer: Subscription | null = null;
    private _touchMode: boolean = false;
    private _writeTimer: Subscription | null = null;

    ngAfterViewInit(): void {
        this.writeValue(this.formControl.value);
    }

    override SortItems() {
        super.SortItems();

        this._allItems = [...this.sortedItemListByGroup.values()].flat();
        this._updatingViewControlValueManually = true;
        this.SubscribeToViewValueChanges();
    }

    override writeValue(value: T | null, remainingAttempts = 4) {
        if (value !== undefined) {
            this._writeTimer?.unsubscribe();

            if (value === null || value === "" || this._allItems.length && this.itemMap.has(this.identifierOf(value))) {
                this.SetSelectedValue(value);
            } else if (remainingAttempts) {
                this._writeTimer = timer(0).subscribe(() => this.writeValue(value, remainingAttempts - 1));
            }
        }
    }

    Touch(event: any) {
        // I cannot get this to work on iPhone despite many hours of trying, due to the fact that it's impossible to programmatically
        // focus the input element on double-click or anything like that. Ergo, touch mode = no type-to-search.
        this._touchMode = true;
        // this.Clicked(event);
    }

    Selected(event: MatAutocompleteSelectedEvent) {
        this._itemSelectedFromDropdown = true;

        const item = this.itemMap?.get(event.option.value)!;
        if (!item) {
            this.SetSelectedValue(null);
            this.SetFormControlValue(null);
            this.formControl.isDirty = true;
        } else {
            this.SetSelectedValue(item.value);
            this.SetFormControlValue(item.value);
        }

        this.OnBlur();
    }

    Clicked(event: any) {
        setTimeout(() => {
            event.preventDefault();

            if (this.formControl.disabled) {
                // Do nothing
            } else if (this._waitingForDoubleClick && !this.touchMode) {
                this._userTyping = true;
                this._waitingForDoubleClick = false;
                setTimeout(() => this.input?.nativeElement.focus());

                if (!this.autocompleteTrigger.panelOpen) {
                    this.autocompleteTrigger.openPanel();
                }
            } else if (!this.autocompleteTrigger.panelOpen) {
                this._waitingForDoubleClick = true;

                if (this.formControl.value) {
                    setTimeout(() => {
                        let index = this._allItems.findIndex(item => item.value === this.formControl.value);
                        if (index >= 0) {
                            this._scrollToOptionFromOpenPanel = true;
                            this.autocompleteTrigger.autocomplete._keyManager.setActiveItem(this.isOptional ? index + 1 : index);
                        }
                    });
                }

                this.autocompleteTrigger.openPanel();
                setTimeout(() => this.input?.nativeElement.focus());
                setTimeout(() => this._waitingForDoubleClick = false, 400);
            } else if (!this._userTyping) {
                this.autocompleteTrigger.closePanel();
                this._waitingForDoubleClick = true;
                setTimeout(() => this._waitingForDoubleClick = false, 400);
            }
        });
    }

    Activated(event: MatAutocompleteActivatedEvent) {
        if (this._scrollToOptionFromOpenPanel) {
            this._scrollToOptionFromOpenPanel = false;
            event.option?._getHostElement().scrollIntoView({
                block: 'nearest'
            });
        }
    }

    PanelOpened() {
        if (this._selectedItem) {
            const identifier = this.identifierOf(this._selectedItem.value);
            this.autocomplete?.options.find(option => option.value === identifier)?.select();
        }
    }


    PanelClosed() {
        if (!this._waitingForDoubleClick && (this._resultSetEmpty || (this.userTyping && !this._itemSelectedFromDropdown))) {
            this.formControl.markAsTouched();

            this.SetSelectedValue();
            this.OnBlur();
        }
    }

    CloseIfNotFocused() {
        const activeElement = document.activeElement;
        if ((!activeElement || activeElement !== this.input?.nativeElement) && this.autocompleteTrigger.panelOpen) {
            this.autocompleteTrigger.closePanel();
        }
    }

    TogglePanel(): void {
        if (this.autocompleteTrigger.panelOpen) {
            this.CloseIfNotFocused();
        } else {
            this.autocompleteTrigger.openPanel();
        }
    }

    private SetSelectedValue(value?: T | null) {
        this.autocomplete?.options.forEach(option => option.deselect());

        if (value === null || value === "") {
            this._selectedItem = null;
            this._selectedItemHTML = '';
        } else if (value !== undefined) {
            const identifier = this.identifierOf(value);
            this._selectedItem = this.itemMap.get(identifier)!;

            const selectedOption = this.autocompleteTrigger?.autocomplete.options.find(option => option.value === identifier);
            this._selectedItemHTML = selectedOption?._getHostElement().querySelector('.sb-select-label-content')?.innerHTML ?? '';
        }
        
        this._userTyping = false;
        this.viewControl.setValue('');
        
        setTimeout(() => {
            this._itemSelectedFromDropdown = false;
            this.viewControl.setValue(' ', { emitEvent: false });
            this._updatingViewControlValueManually = true;
            this.viewControl.setValue('');
        }, 500);
    }

    private SubscribeToViewValueChanges(): void {
        this._filteredItemsByGroup = this.viewControl.valueChanges.pipe(
            startWith(''),
            filter(_ => this._updatingViewControlValueManually),
            map((value: string) => {
                this._updatingViewControlValueManually = false;

                const items = value ? this.Filter(value) : [...this._allItems];

                // Cannot limit this list because mobile users can't type to filter
                // TODO: create a lazy loading paradigm that avoids rendering the entire list 
                // if (items.length > 1000) {  // Limit autocomplete list to avoid performance degradation
                //     items.length = 1000;
                // }

                const itemsByGroup = new Map<IItemGroup | null, FormFieldSelectItemComponent<T>[]>();
                this.sortedItemListByGroup?.forEach((_, group) => {
                    itemsByGroup.set(group, []);
                });

                items.forEach(item => {
                    itemsByGroup.get(item.group)!.push(item);
                });

                this._resultSetEmpty = !items.length;
                this.formControl.markAsUntouched();

                this._timer?.unsubscribe();
                if (this._userTyping && !this.resultSetEmpty) {
                    // Stop bouncing to bottom on every character when an element further down was selected before typing started
                    document.getElementsByClassName('mat-mdc-autocomplete-panel')[0]?.scrollTo({top: 0});
                    this._timer = timer(250).subscribe(() => {
                        this._timer = null;
                        const activeItem = this.autocompleteTrigger.autocomplete._keyManager.activeItem;
                        if (activeItem) {
                            activeItem._getHostElement().scrollIntoView({
                                block: 'nearest'
                            });
                        } else {
                            this.autocompleteTrigger.autocomplete._keyManager.setActiveItem(0);
                        }
                    });
                }

                return itemsByGroup;
            })
        );
    }

    private Filter(value: string): FormFieldSelectItemComponent<T>[] {
        const checkValue = value.trim().toLocaleLowerCase();
        return this._allItems.filter(item => this.nameOf!(item.value)?.toLocaleLowerCase().includes(checkValue));
    }

    get filteredItemsByGroup(): Observable<Map<IItemGroup | null, FormFieldSelectItemComponent<T>[]>> {
        return this._filteredItemsByGroup;
    }

    get resultSetEmpty(): boolean {
        return this._resultSetEmpty;
    }

    get userTyping(): boolean {
        return this._userTyping;
    }

    get filterText(): string {
        return $localize`:@@CommonFormFieldTypeToFilter:Filter options...`;
    }

    get selectedItemHTML(): string {
        return this._selectedItemHTML;
    }

    get selectedItem(): FormFieldSelectItemComponent<T> | null {
        return this._selectedItem;
    }

    get waitingForDoubleClick(): boolean {
        return this._waitingForDoubleClick;
    }

    get itemSelectedFromDropDown(): boolean {
        return this._itemSelectedFromDropdown;
    }

    get touchMode(): boolean {
        return this._touchMode;
    }

    @HostListener('keydown', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent) {
        if (['Enter', 'ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End'].includes(event.key)) {
            if (this.autocompleteTrigger.panelOpen) {
                this._timer = null;

                const index = this.autocompleteTrigger.autocomplete._keyManager.activeItemIndex;
                if (!this._resultSetEmpty && index !== null) {
                    const maxIndex = this.autocompleteTrigger.autocomplete.options.length - 1;

                    let newIndex = index;
                    switch (event.key) {
                        case 'PageUp': newIndex = Math.max(index - 5, 0); break;
                        case 'PageDown': newIndex = Math.min(index + 5, maxIndex); break;
                        case 'Home': newIndex = 0; break;
                        case 'End': newIndex = maxIndex; break;
                    }

                    if (newIndex !== index) {
                        this._scrollToOptionFromOpenPanel = true;
                        this.autocompleteTrigger.autocomplete._keyManager.setActiveItem(newIndex);
                    }
                }
                event.preventDefault();
            } else if (!this._itemSelectedFromDropdown) {
                this.Clicked(event);
            }
        } else if (!['Tab', 'Escape'].includes(event.key) && !this.formControl.disabled) {
            if (!this._userTyping) {
                this.input?.nativeElement.focus();
                this._userTyping = true;
                this.viewControl.setValue(' ', { emitEvent: false });
                this.viewControl.setValue('');     // Make sure we fire the handler even if the user opens the panel using e.g. backspace
            }

            this._updatingViewControlValueManually = true;
        }
    }
}